'use client';

import { Suspense, useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { concat, find, map, merge, size } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import Button from '@/components/shared/Button';
import { Pagination } from '@/components/ui/Pagination';
import { BrokerListItem } from '@/components/ui/ProductLayout/containers/FindBrokersContainer/BrokerListItem';
import { BrokerListItem as MobileBrokerListItem } from '@/components/ui/ProductLayout/containers/FindBrokersContainer/Mobile/BrokerListItem';
import type {
  BrokerContact as BrokerContactType,
  Pagination as PaginationType,
  SelectOption
} from '@/graphql/common/types';
import type { FindBrokersResponse } from '@/graphql/queries/pages/encontre-um-corretor/types';
import { Select } from '@axa/design-system';
import { EmptyState } from './EmptyState';
import { LoadingList } from './LoadingList';

type FindBrokerProps = {
  title?: string;
  description?: string;
  initialBrokers?: BrokerContactType[];
  initialPagination?: PaginationType;
  categoryOptions?: SelectOption[];
  productOptions?: SelectOption[];
  stateOptions?: SelectOption[];
  cityOptions?: SelectOption[];
};

export const FindBroker = ({
  title = 'Encontre corretores AXA',
  description,
  initialBrokers = [],
  initialPagination,
  categoryOptions,
  productOptions,
  stateOptions,
  cityOptions
}: FindBrokerProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const searchParams = useSearchParams();
  const router = useRouter();

  const [fetchedBrokers, setFetchedBrokers] = useState<BrokerContactType[]>([]);
  const [fetchedPagination, setFetchedPagination] = useState<PaginationType>();
  const [loading, setLoading] = useState(false);

  const brokers = concat(initialBrokers, fetchedBrokers);
  const pagination = merge(initialPagination, fetchedPagination);

  const [category, setCategory] = useState<SelectOption | undefined>(() => {
    const paramCategory = searchParams.get('categoria');

    const categoryFound = find(categoryOptions, (item) => item.value === paramCategory);

    if (!categoryFound) return undefined;

    return categoryFound;
  });
  const [product, setProduct] = useState<SelectOption | undefined>(() => {
    const paramCategory = searchParams.get('categoria');

    if (!paramCategory) return undefined;

    const paramProduct = searchParams.get('seguro');

    const secureFound = find(productOptions, (item) => item.value === paramProduct);

    if (!secureFound) return undefined;

    return secureFound;
  });
  const [state, setState] = useState<SelectOption | undefined>(() => {
    const paramCategory = searchParams.get('categoria');
    const paramProduct = searchParams.get('seguro');
    const paramState = searchParams.get('estado');

    if (!paramCategory || !paramProduct || !paramState) return undefined;

    const stateFound = find(stateOptions, (item) => item.value === paramState);

    if (!stateFound) return undefined;

    return stateFound;
  });
  const [city, setCity] = useState<SelectOption | undefined>(() => {
    const paramCategory = searchParams.get('categoria');
    const paramProduct = searchParams.get('seguro');
    const paramState = searchParams.get('estado');
    const paramCity = searchParams.get('cidade');

    if (!paramCategory || !paramProduct || !paramState || !paramCity) return undefined;

    const cityFound = find(cityOptions, (item) => item.value === paramCity);

    if (!cityFound) return undefined;

    return cityFound;
  });

  const handleChangeCategory = useCallback(
    (value?: SelectOption) => {
      setCategory(value);
      setProduct(undefined);
      setState(undefined);
      setCity(undefined);
      setFetchedBrokers([]);
      setFetchedPagination(undefined);

      if (value?.value) {
        router.push(`/encontre-um-corretor?categoria=${value?.value}`);
      } else {
        router.push('/encontre-um-corretor');
      }
    },
    [router]
  );

  const handleChangeProduct = useCallback(
    (value?: SelectOption) => {
      setProduct(value);
      setState(undefined);
      setCity(undefined);
      setFetchedBrokers([]);
      setFetchedPagination(undefined);

      if (value?.value) {
        router.push(`/encontre-um-corretor?categoria=${category?.value}&seguro=${value?.value}`);
      } else {
        router.push(`/encontre-um-corretor?categoria=${category?.value}`);
      }
    },
    [router, category]
  );

  const handleChangeState = useCallback(
    (value?: SelectOption) => {
      setState(value);
      setCity(undefined);
      setFetchedBrokers([]);
      setFetchedPagination(undefined);

      if (value?.value) {
        router.push(
          `/encontre-um-corretor?categoria=${category?.value}&seguro=${product?.value}&estado=${value?.value}`
        );
      } else {
        router.push(`/encontre-um-corretor?categoria=${category?.value}&seguro=${product?.value}`);
      }
    },
    [router, category, product]
  );

  const handleChangeCity = useCallback(
    (value?: SelectOption) => {
      setCity(value);
      setFetchedBrokers([]);
      setFetchedPagination(undefined);

      if (value?.value) {
        router.push(
          `/encontre-um-corretor?categoria=${category?.value}&seguro=${product?.value}&estado=${state?.value}&cidade=${value?.value}`
        );
      } else {
        router.push(
          `/encontre-um-corretor?categoria=${category?.value}&seguro=${product?.value}&estado=${state?.value}`
        );
      }
    },
    [router, category, product, state]
  );

  const handleLoadMore = async () => {
    setLoading(true);

    try {
      let url = `/api/brokers/load-more?page=${(pagination?.page || 0) + 1}`;

      if (category?.value) url += `&category=${category?.value}`;
      if (product?.value) url += `&product=${product?.value}`;

      if (state?.value === 'online') {
        url += '&onlineService=true';
      } else if (state?.value) {
        url += `&state=${state?.value}`;
        if (city?.value) url += `&city=${city?.value}`;
      }

      const response = await fetch(url, {
        method: 'GET'
      });
      const data = (await response.json()) as FindBrokersResponse;

      setFetchedBrokers((oldState) => [...oldState, ...(data?.brokerContacts?.data || [])]);
      setFetchedPagination(data?.brokerContacts?.pageInfo);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const BrokerComponent = isMobile ? MobileBrokerListItem : BrokerListItem;

  return (
    <section className="px-6 py-12 lg:px-12 lg:py-20 xl:p-20">
      <div className="flex flex-col gap-2 xl:gap-4">
        <h1 className="font-publico text-2xl font-bold text-blue-500 lg:text-[2rem] lg:leading-10 xl:text-[2.5rem] xl:leading-[3rem]">
          {title}
        </h1>
        {description && (
          <p className="text-base font-regular text-grey-700 xl:text-xl">{description}</p>
        )}
      </div>

      <div className="flex flex-col gap-4 pt-6 lg:flex-row lg:pt-12">
        <Select
          id="category-select"
          label="Segmento"
          placeholder="Selecione"
          searchable
          options={categoryOptions}
          onChange={handleChangeCategory}
          value={category}
        />
        <Select
          id="product-select"
          label="Produto"
          placeholder="Selecione"
          searchable
          options={productOptions}
          onChange={handleChangeProduct}
          value={product}
          disabled={!category}
        />
        <Select
          id="state-select"
          label="Selecione seu estado"
          placeholder="Selecione"
          searchable
          options={stateOptions}
          onChange={handleChangeState}
          value={state}
          disabled={!product}
        />
        <Select
          id="city-select"
          label="Selecione sua cidade"
          placeholder="Selecione"
          searchable
          options={cityOptions}
          onChange={handleChangeCity}
          value={city}
          disabled={!state || state.value === 'online'}
        />
      </div>

      <Suspense fallback={<LoadingList />}>
        {size(brokers) > 0 ? (
          <div className="pt-8 lg:pt-6">
            {map(brokers, (broker) => (
              <BrokerComponent key={broker.id} {...broker} />
            ))}

            {isMobile ? (
              <>
                {(pagination?.page || 0) < (pagination?.pageCount || 0) && (
                  <section className="flex justify-center py-12 lg:py-20">
                    <Button onClick={handleLoadMore} disabled={loading}>
                      Mostrar mais
                    </Button>
                  </section>
                )}
              </>
            ) : (
              <Pagination
                currentPage={pagination?.page}
                itemsPerPage={pagination?.pageSize}
                totalItems={pagination?.total || 0}
                className="py-12 lg:py-20"
                scroll
              />
            )}
          </div>
        ) : (
          <EmptyState hasInitialParams />
        )}
      </Suspense>
    </section>
  );
};
